import React, { useState } from 'react'
import DashboardLayout from '../../components/layouts/dashboard'
import { formatDateInWords, formatNewDateInWords } from '../../utils/functions'
import { useParams } from 'react-router-dom'
import {
  useAddAnnouncementComment,
  useAddAnnouncementReaction,
  useGetSingleAnnouncement,
} from '../../redux/employee/hook/announcement'
import LoadingBar from '../../components/loader-bar'
import ButtonSVG from '../../assets/svg/hr/send-button.svg'
import PlaceHolderSVG from '../../assets/svg/hr/placeholder-img.svg'
import { useSelector } from 'react-redux'

const ViewAnnouncements = () => {
  const [comment, setComment] = useState(null)
  const { id } = useParams()
  const { user } = useSelector((state) => state.userSlice)

  const { mutateAsync: addComment, isLoading: commentLoading } =
    useAddAnnouncementComment()
  const { mutateAsync: addReactions, isLoading: reactionLoading } =
    useAddAnnouncementReaction()
  const {
    data: announcement,
    isLoading,
    refetch,
  } = useGetSingleAnnouncement(id)

  const handleComment = async () => {
    if (!comment?.trim()) {
      return
    }
    try {
      await addComment({
        id: announcement?.id,
        data: { comment: comment },
      }).then(() => {
        refetch()
        setComment('')
      })
    } catch (error) {}
  }

  const handleReaction = async (reactionType) => {
    try {
      const currentReaction = announcement?.current_user_reaction?.reaction || 0
      let reactionToSend = 0

      // If current reaction is 0 and clicking thumbs up, send 2
      if (currentReaction === 0 && reactionType === 'up') {
        reactionToSend = 2
      }
      // If current reaction is 0 and clicking thumbs down, send 1
      else if (currentReaction === 0 && reactionType === 'down') {
        reactionToSend = 1
      }
      // If toggling thumbs up (current reaction is 2), send 0
      else if (currentReaction === 2 && reactionType === 'up') {
        reactionToSend = 0
      }
      // If toggling thumbs down (current reaction is 1), send 0
      else if (currentReaction === 1 && reactionType === 'down') {
        reactionToSend = 0
      }
      // If switching from thumbs down to thumbs up
      else if (currentReaction === 1 && reactionType === 'up') {
        reactionToSend = 2
      }
      // If switching from thumbs up to thumbs down
      else if (currentReaction === 2 && reactionType === 'down') {
        reactionToSend = 1
      }

      await addReactions({
        id: announcement?.id,
        data: { reaction: reactionToSend },
      }).then(() => {
        // refetch() // Refetch to get updated reaction state and counts
      })
    } catch (error) {
      console.error('Error adding reaction:', error)
    }
  }

  return (
    <DashboardLayout title='Announcements'>
      <>
        {isLoading ? (
          <div className='w-full items-center justify-center h-[500px]'>
            <LoadingBar loading={isLoading} />
          </div>
        ) : (
          <div className='flex md:flex-row flex-col gap-5 items-start w-full bg-white p-3 rounded'>
            <section className='flex flex-col gap-2 w-full border-none md:border-r md:border-r-gray-200'>
              <p>
                By{' '}
                {announcement?.announcer?.first_name &&
                announcement.announcer?.last_name
                  ? `${announcement.announcer.first_name} ${announcement.announcer.last_name}`
                  : user?.company?.company_name}{' '}
                | {formatNewDateInWords(announcement?.publish_date)}{' '}
              </p>
              <p className='text-base font-semibold'>{announcement?.title}</p>
              <img
                src={announcement?.attachment ?? PlaceHolderSVG}
                alt='centelHR'
                className='max-w-[500px] max-h-[300px] object-cover'
              />

              <p className='text-base max-w-[700px]'>{announcement?.content}</p>
              <div className='flex flex-col items-start gap-2'>
                <p className='text-base font-semibold'>Reactions</p>
                <div className='flex items-center gap-3 border rounded p-2'>
                  <div className='flex flex-col gap-1 items-center'>
                    <button
                      onClick={() => handleReaction('up')}
                      disabled={reactionLoading}
                    >
                      {announcement?.current_user_reaction?.reaction === 2
                        ? '👍'
                        : '👍🏿'}{' '}
                    </button>
                    <span>{announcement?.reaction_counts?.thumbs_up ?? 0}</span>
                  </div>
                  <div className='flex flex-col gap-1 items-center'>
                    <button
                      onClick={() => handleReaction('down')}
                      disabled={reactionLoading}
                    >
                      {announcement?.current_user_reaction?.reaction === 1
                        ? '👎'
                        : '👎🏿'}
                    </button>
                    <span>
                      {announcement?.reaction_counts?.thumbs_down ?? 0}
                    </span>
                  </div>
                </div>
              </div>
            </section>
            <section className='flex flex-col w-full items-start'>
              <div className='flex items-center gap-1 mb-3'>
                <h1 className='text-sm font-medium'>Comments </h1>

                <span className='text-white text-xs bg-error h-5 w-5 flex items-center justify-center p-2 rounded-full'>
                  {announcement?.comments_count}
                </span>
              </div>

              <main className='flex flex-col items-start gap-2 w-full'>
                {announcement?.comments?.length > 0 ? (
                  <section className='h-[400px] w-full overflow-y-auto'>
                    {announcement?.comments?.map((comment, index) => (
                      <main
                        key={index}
                        className='flex flex-col gap-1  border-y  w-full border-y-gray-200 p-2'
                      >
                        <h1 className='text-sm max-w-[500px] font-medium'>
                          {comment.comment}
                        </h1>
                        <p className='text-sm  text-gray-600'>
                          {comment.profile?.first_name &&
                          comment.profile?.last_name
                            ? `${comment.profile.first_name} ${comment.profile.last_name}`
                            : user?.company?.company_name}{' '}
                          | {formatDateInWords(comment?.created)}
                        </p>
                      </main>
                    ))}
                  </section>
                ) : (
                  <p className='text-sm flex text-left items-center w-full h-[400px] font-bold text-[18px] text-black'>
                    No comments yet
                  </p>
                )}
              </main>
              <section className='my-3 w-full'>
                <p className='text-sm text-black font-semibold'>Add Comment</p>
                <div className='flex w-full items-center rounded-md px-2 my-2 border h-11'>
                  <input
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder='Add a comment'
                    type='text'
                    className='outline-none bg-transparent border-none w-full text-sm font-medium text-black'
                  />
                  <button
                    onClick={handleComment}
                    disabled={commentLoading || !comment?.trim()}
                    type='submit'
                    className='bg-hr-primary-1 cursor-pointer rounded-full p-2'
                  >
                    <img src={ButtonSVG} alt='CentelHR' />
                  </button>
                </div>
              </section>
            </section>
          </div>
        )}
      </>
    </DashboardLayout>
  )
}

export default ViewAnnouncements
